import React, { useEffect } from 'react';
import { useData } from '../../context/DataContext';
import { levelLabel } from '../../utils/calculations';
import { fitA4Sheet, resetA4Fit } from '../../utils/printUtils';

export const StudentReportCardModal = ({ student, onClose, onOpenQuestionPaper }) => {
  const { loMap, mathResults, englishResults } = useData();

  

  useEffect(() => {
    const handleBeforePrint = () => {
      const fitEl = document.getElementById('report-a4-fit');
      if (fitEl) fitA4Sheet(fitEl);
    };

    const handleAfterPrint = () => {
      const fitEl = document.getElementById('report-a4-fit');
      if (fitEl) resetA4Fit(fitEl);
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);
if (!student) return null;

  const handlePrint = () => {
    window.print();
  };

  const getPerformanceLabel = (mastery, grade) => {
    const target = Math.min(7, grade);
    const gap = target - mastery;
    if (gap <= 0) {
      return {
        text: 'Competent',
        borderStyle: 'border-l-4 border-green-500',
        textColor: '#166534',
        badgeBg: '#16a34a',
        icon: '🌟'
      };
    } else if (gap <= 2) {
      return {
        text: 'Bridge the Gap',
        borderStyle: 'border-l-4 border-orange-500',
        textColor: '#b45309',
        badgeBg: '#f59e0b',
        icon: '📈'
      };
    } else {
      return {
        text: 'Remediation Need',
        borderStyle: 'border-l-4 border-red-500',
        textColor: '#b91c1c',
        badgeBg: '#dc2626',
        icon: '🚨'
      };
    }
  };

  const engStatus = getPerformanceLabel(student.englishMastery, student.registeredGrade);
  const mathStatus = getPerformanceLabel(student.mathMastery, student.registeredGrade);

  // Compute breakdown logs for English & Math
  const computeSubjectBreakdown = (subjectName, resultsMap) => {
    const data = resultsMap.get(student.id);
    if (!data) return { levels: [], totalScore: 0 };

    const totalQuestions = data.answers.filter(a => !isNaN(a) && a !== null).length;
    const totalCorrect = data.answers.filter(a => a === 1).length;
    const totalScore = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

    // Group answers by tested grade level
    const levelStats = {};
    data.answers.forEach((ans, idx) => {
      if (isNaN(ans)) return;
      const qNum = idx + 1;
      const lvl = 3 + Math.floor((qNum - 1) / 5); // standard level mapping fallback or direct
      if (!levelStats[lvl]) levelStats[lvl] = { correct: 0, total: 0 };
      levelStats[lvl].total++;
      if (ans === 1) levelStats[lvl].correct++;
    });

    const levels = Object.keys(levelStats).map(l => {
      const s = levelStats[l];
      const pct = s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0;
      return { level: parseInt(l), pct, passed: pct >= 80, weak: pct >= 60 && pct < 80 };
    });

    return { levels, totalScore };
  };

  const engBreakdown = computeSubjectBreakdown('English', englishResults);
  const mathBreakdown = computeSubjectBreakdown('Math', mathResults);
  const overallAvg = Math.round((engBreakdown.totalScore + mathBreakdown.totalScore) / 2);
  // Build LO breakdown list
  const getSubjectLOs = (subjectName, resultsMap) => {
    const data = resultsMap.get(student.id);
    if (!data || loMap.size === 0) return [];

    const stats = {};
    data.answers.forEach((ans, idx) => {
      if (isNaN(ans)) return;
      const qNum = idx + 1;
      const lo = loMap.get(`${subjectName}-${student.registeredGrade}-${qNum}`);
      if (lo) {
        if (!stats[lo]) stats[lo] = { correct: 0, total: 0, questions: [] };
        stats[lo].total++;
        const isCorrect = ans === 1;
        if (isCorrect) stats[lo].correct++;
        stats[lo].questions.push({ q: qNum, correct: isCorrect });
      }
    });

    return Object.keys(stats).sort().map(lo => {
      const s = stats[lo];
      const perc = s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0;
      return { name: lo, perc, questions: s.questions };
    });
  };

  const engLOs = getSubjectLOs('English', englishResults);
  const mathLOs = getSubjectLOs('Math', mathResults);
  return (
    <div
      id="report-modal"
      className="fixed inset-0 z-50 bg-gray-100 flex flex-col h-full w-full select-none " 
      style={{marginTop:"11rem"}}
      // style={{position:"relative",top:"5rem"}}
      aria-labelledby="report-modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Top action header */}
      <div className="no-print flex justify-between items-center gap-3 px-3 sm:px-6 py-3 bg-white border-b border-gray-200 shadow-sm shrink-0">
        <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate" id="report-modal-title">
          Report Card — {student.name}
        </h3>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={handlePrint}
            className="px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 flex items-center gap-2 text-sm cursor-pointer"
          >
            <span>🖨️</span> <span className="hidden sm:inline">Print</span>
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-bold text-sm cursor-pointer"
          >
            ✕ <span className="hidden sm:inline">Close</span>
          </button>
        </div>
      </div>

      {/* Sheet viewport */}
      <div id="report-modal-scroll" className="flex-1 overflow-y-auto px-2 sm:px-4 py-4 sm:py-6">
        <div id="report-a4-sheet" className="report-a4-sheet">
          <div id="report-a4-fit" className="report-a4-fit">
            {/* Header Box with Primary Brand Green Border */}
            <div
              className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg mb-6 print-avoid-break"
              style={{ border: '2px solid #347363' }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_1fr] items-center gap-3 sm:gap-4 print:!grid-cols-[1fr_2fr_1fr] print:!gap-4">
                <div className="flex items-center justify-center sm:justify-start space-x-3">
                  <img
                    src="https://www.northsouth.org/wp-content/uploads/2024/09/nsf-logov1.png"
                    alt="School Logo"
                    className="h-14 w-auto rounded-lg shadow-md bg-white p-1"
                    onError={(e) => { e.target.src = 'https://placehold.co/100x100?text=LOGO'; }}
                  />
                </div>
                <div className="text-center">
                  <div
                    className="font-bold uppercase"
                    style={{
                      // fontFamily: "Georgia, 'Times New Roman', serif",
                      fontSize: '24px',
                      letterSpacing: '0.5px',
                      color: '#357a64'
                    }}
                  >
                    Student Report Card
                  </div>
                  <div className="text-xs text-gray-400 mt-1">SAMAGRA-1 Assessment 2026-27</div>
                </div>
                <div className="flex justify-center sm:justify-end">
                  <button
                    onClick={() => onOpenQuestionPaper(student.registeredGrade)}
                    className="no-print bg-gray-50 hover:bg-gray-100 text-gray-700 font-bold py-2 px-3 rounded-lg flex items-center gap-2 transition-colors border border-gray-200 text-xs cursor-pointer"
                  >
                    <span>📄</span> <span>Grade {student.registeredGrade} Question Paper</span>
                  </button>
                </div>
              </div>

              <div className="h-px bg-gray-100 my-4"></div>

              {/* Student Metadata */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 print:!grid-cols-4 print:!gap-6">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">Student Name</p>
                  <p className="text-lg font-bold text-gray-900">{student.name}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">Grade &amp; Section</p>
                  <p className="text-base font-bold text-gray-900">
                    Grade {student.registeredGrade} - {student.section || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">Student ID</p>
                  <p className="text-base font-bold text-gray-900">{student.id}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">School</p>
                  <p className="text-base font-bold text-gray-900">{student.school}</p>
                </div>
              </div>
            </div>

            {/* Overall Performance: 3 KPI cards */}
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Overall Performance</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 print:!grid-cols-3">
                <div className="bg-white rounded-2xl p-4 shadow border-l-4" style={{ borderColor: '#1e3a5f' }}>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Overall Average</p>
                  <p className="text-3xl font-black mt-1" style={{ color: '#1e3a5f' }}>
                    {overallAvg}%
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">Combined English &amp; Math</p>
                </div>
                <div className="bg-white rounded-2xl p-4 shadow border-l-4" style={{ borderColor: '#2a6a4a' }}>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">English Average</p>
                  <p className="text-3xl font-black mt-1" style={{ color: '#2a6a4a' }}>
                    {engBreakdown.totalScore}%
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">Assessment score</p>
                </div>
                <div className="bg-white rounded-2xl p-4 shadow border-l-4" style={{ borderColor: '#1e3a9a' }}>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Math Average</p>
                  <p className="text-3xl font-black mt-1" style={{ color: '#1e3a9a' }}>
                    {mathBreakdown.totalScore}%
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">Assessment score</p>
                </div>
              </div>
            </div>

            {/* Subject Performance */}
            <div className="mb-6">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                <h3 className="text-xl font-bold text-gray-800">Subject Performance</h3>
                <div className="flex flex-wrap gap-4 text-xs font-semibold">
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block bg-green-600"></span>
                    <span className="text-gray-700">≥80% passed</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block bg-orange-500"></span>
                    <span className="text-gray-700">60-79% — forgiven</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block bg-red-600"></span>
                    <span className="text-gray-700">below 60%</span>
                  </span>
                </div>
              </div>
              <p className="text-xs italic text-gray-400 mb-3">
                *A single 60-79% level (✓ in orange) still counts as passed if every other level is ≥80%.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 print:!grid-cols-2 print:!gap-6">
                {/* English Box */}
                <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg border-l-4 border-green-500 print-avoid-break">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">📚 English Performance</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-semibold">Registered Grade:</span>
                      <span className="font-bold text-gray-800">Grade {student.registeredGrade}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-semibold">Mastery Level Achieved:</span>
                      <span className="font-black text-xl" style={{ color: '#16a34a' }}>
                        {student.englishMastery >= Math.min(7, student.registeredGrade)
                          ? 'At Grade Level'
                          : levelLabel(student.englishMastery)}
                      </span>
                    </div>
                    <div className="mt-4 p-3 bg-green-50 rounded-lg">
                      <div className="flex flex-wrap gap-2 text-xs font-semibold">
                        {engBreakdown.levels.map(lvl => (
                        
                         (englishResults.get(student.id).stats[lvl.level])?.correct &&
                        <span
                            key={lvl.level}
                            className={`px-2 py-1 w-100 dflex JSBetween alignitems rounded ${
                              lvl.passed
                                ? 'bg-green-200 text-green-800 font-bold'
                                : lvl.weak
                                ? 'bg-orange-200 text-orange-800 font-bold'
                                : 'bg-red-200 text-red-800 font-bold'
                            }`}
                          >
                           <span> Grade{lvl.level}:</span> <span>{(englishResults.get(student.id).stats[lvl.level])?.correct}/{(mathResults.get(student.id).stats[lvl.level])?.total} ({lvl.pct}%) {lvl.passed ? '✓' : lvl.weak ? '✓*' : '✗'}</span>
                            </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Math Box */}
                <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg border-l-4 border-blue-500 print-avoid-break">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">📐 Mathematics Performance</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-semibold">Registered Grade:</span>
                      <span className="font-bold text-gray-800">Grade {student.registeredGrade}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-semibold">Mastery Level Achieved:</span>
                      <span className="font-black text-xl" style={{ color: '#2563eb' }}>
                        {student.mathMastery >= Math.min(7, student.registeredGrade)
                          ? 'At Grade Level'
                          : levelLabel(student.mathMastery)}
                      </span>
                    </div>
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <div className="flex flex-wrap gap-2 text-xs font-semibold">
                        {mathBreakdown.levels.map(lvl => (
                          (mathResults.get(student.id).stats[lvl.level])?.correct &&
                          <span
                            key={lvl.level}
                            className={`px-2 py-1 w-100 dflex JSBetween alignitems rounded ${
                              lvl.passed
                                ? 'bg-green-200 text-green-800 font-bold'
                                : lvl.weak
                                ? 'bg-orange-200 text-orange-800 font-bold'
                                : 'bg-red-200 text-red-800 font-bold'
                            }`}
                          >
                           <span>Grade{lvl.level}:</span>  <span>{(mathResults.get(student.id).stats[lvl.level])?.correct}/{(mathResults.get(student.id).stats[lvl.level])?.total} {lvl.pct}% {lvl.passed ? '✓' : lvl.weak ? '✓*' : '✗'}</span>
                          </span>
                          // {lvl.stats[lvl.level].correct}/{lvl.stats[lvl.level].total} {mathResults.get(student.id)?.stats?.[lvl.level]}
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Insights */}
            <div className="mb-6">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                <h3 className="text-xl font-bold text-gray-800">Performance Insights</h3>
                <div className="flex flex-wrap gap-4 text-xs font-semibold">
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#16a34a' }}></span>
                    <span className="text-gray-700"><b style={{ color: '#166534' }}>Competent</b> passed all levels</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#f59e0b' }}></span>
                    <span className="text-gray-700"><b style={{ color: '#b45309' }}>Bridge the Gap</b> 1-2 yrs below</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#dc2626' }}></span>
                    <span className="text-gray-700"><b style={{ color: '#b91c1c' }}>Remediation Need</b> 3+ yrs below</span>
                  </span>
                </div>
              </div>
              <p className="text-xs italic text-gray-400 mb-3">*A single weak level (60-79%) is forgiven if every other level is ≥80%.</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 print:!grid-cols-2">
                {/* English Status Card */}
                <div className={`bg-white p-4 rounded-xl shadow ${engStatus.borderStyle}`}>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">English Status</p>
                  <p className="text-xl font-black mt-1" style={{ color: engStatus.textColor }}>
                    {engStatus.icon} {engStatus.text}
                  </p>
                </div>
                {/* Math Status Card */}
                <div className={`bg-white p-4 rounded-xl shadow ${mathStatus.borderStyle}`}>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Math Status</p>
                  <p className="text-xl font-black mt-1" style={{ color: mathStatus.textColor }}>
                    {mathStatus.icon} {mathStatus.text}
                  </p>
                </div>
              </div>
            </div>

            {/* Learning Objectives Breakdown */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 print:!grid-cols-2 print:!gap-6">
              {/* English LO block */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg border-2 border-green-100 print-avoid-break">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>📚</span> English Learning Objectives
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr className="bg-green-50 text-green-800">
                        <th className="text-left py-2 px-3 font-bold border" style={{ width: '45%' }}>Learning Objective</th>
                        <th className="text-center py-2 px-2 font-bold border" style={{ width: '18%' }}>Score</th>
                        <th className="text-left py-2 px-3 font-bold border" style={{ width: '37%' }}>Questions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {engLOs.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center py-4 text-gray-500">No data.</td>
                        </tr>
                      ) : (
                        engLOs.map((lo, i) => (
                          <tr key={i} className="border-b">
                            <td className="py-2 px-3 font-medium text-gray-800 truncate" title={lo.name}>{lo.name}</td>
                            <td className="py-2 px-2 text-center font-bold" style={{ color: lo.perc >= 80 ? '#16a34a' : '#dc2626' }}>
                              {lo.perc}%
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex flex-wrap gap-1">
                                {lo.questions.map(q => (
                                  <span
                                    key={q.q}
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                      q.correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    Q{q.q}{q.correct ? '✓' : '✗'}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Math LO block */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg border-2 border-blue-100 print-avoid-break">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>📐</span> Math Learning Objectives
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr className="bg-blue-50 text-gray-800">
                        <th className="text-left py-2 px-3 font-bold border" style={{ width: '45%' }}>Learning Objective</th>
                        <th className="text-center py-2 px-2 font-bold border" style={{ width: '18%' }}>Score</th>
                        <th className="text-left py-2 px-3 font-bold border" style={{ width: '37%' }}>Questions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mathLOs.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center py-4 text-gray-500">No data.</td>
                        </tr>
                      ) : (
                        mathLOs.map((lo, i) => (
                          <tr key={i} className="border-b">
                            <td className="py-2 px-3 font-medium text-gray-800 truncate" title={lo.name}>{lo.name}</td>
                            <td className="py-2 px-2 text-center font-bold" style={{ color: lo.perc >= 80 ? '#16a34a' : '#dc2626' }}>
                              {lo.perc}%
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex flex-wrap gap-1">
                                {lo.questions.map(q => (
                                  <span
                                    key={q.q}
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                      q.correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    Q{q.q}{q.correct ? '✓' : '✗'}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Print Footer */}
            <div className="mt-8 pt-4 border-t border-dashed border-gray-200 text-xs text-gray-400 leading-relaxed">
              <p className="font-semibold text-gray-500">Generated by NorthSouth Foundation • SAMAGRA-1 Assessment</p>
              <p>Confidential Student Record</p>
              <p>Printed on: <span className="font-semibold text-gray-500">{new Date().toLocaleDateString()}</span></p>
              <a href="https://www.northsouth.org" target="_blank" rel="noopener noreferrer" className="font-bold" style={{ color: '#347363' }}>
                www.northsouth.org
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default StudentReportCardModal;
